import { useSteppedFormComponents, useSteppedFormControls } from '@mews-commander/form';
import { lt } from '@mews/framework';
import { OptimusProvider } from '@mews-commander/ui';
import { Container, Stack } from '@optimus-web/core';
import { PageFooter } from '@mews/b2b-ui';
import { useCurrentEnterpriseAttachments } from '../../common/hooks/useCurrentEnterpriseAttachments';
import { GenerateContractFormStepName, GenerateContractFormValues } from './types';
import { CancelButton } from './CancelButton';
import { generateContractFormSchema, generateContractFormSteps } from './schema';
import { TemplatesStepForm } from './TemplatesStepForm/TemplatesStepForm';
import { DEFAULT_VALUES } from './constants';
import { RecipientsStepForm } from './RecipientsStepForm/RecipientsStepForm';

const useMutatePlaceholder = () => ({
    mutateAsync: async () => Promise.resolve(),
    isPending: false,
});

export const GenerateContractSteppedForm = () => {
    const { StepProgressIndicator, SteppedPrimaryActions, state } = useSteppedFormComponents({
        steps: generateContractFormSteps,
    });

    const { mutateAsync: addContractMutation, isPending: isAddMutationPending } = useMutatePlaceholder();

    const existingAttachmentNames = useCurrentEnterpriseAttachments().map((attachment) => attachment.customName);

    const { renderFormContent, submit } = useSteppedFormControls<
        GenerateContractFormStepName,
        GenerateContractFormValues
    >({
        steps: generateContractFormSteps,
        ...state,
        stepResolvers: generateContractFormSchema(existingAttachmentNames),
        defaultValues: DEFAULT_VALUES,
        stepHandlers: {
            templates: (values, { setCurrentStep }) => {
                setCurrentStep(GenerateContractFormStepName.Recipients);
            },
        },
        onSubmit: async () => {
            // TODO: Implement the logic to handle form submission
            await addContractMutation();

            return true;
        },
    });

    return (
        <OptimusProvider density="spacious">
            <Container padding={['0', '0', '1200', '0']}>
                <Stack vertical itemAlignment="center" spacing="400">
                    <Container maxWidth="700px" width="100%">
                        <StepProgressIndicator
                            stepLabels={{
                                templates: lt('ContractsGenerateContractTemplatesStepLabel'),
                                recipients: lt('ContractsGenerateContractRecipientsStepLabel'),
                            }}
                        />
                    </Container>
                    <Container maxWidth="600px" width="100%">
                        {renderFormContent(({ matchSteps }) =>
                            matchSteps({
                                templates: () => <TemplatesStepForm />,
                                recipients: () => <RecipientsStepForm />,
                            })
                        )}
                    </Container>
                </Stack>
                <PageFooter>
                    <CancelButton />
                    <SteppedPrimaryActions
                        disabled={isAddMutationPending}
                        handleSubmit={submit}
                        saveLabel={lt('ContractsGenerateContractGenerateContractButtonLabel')}
                    />
                </PageFooter>
            </Container>
        </OptimusProvider>
    );
};
